import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { OccasionalSlotsPlaces, RegularSlot } from '@/types/Slots'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import { CalendarMonth, ChildCareOutlined, DoDisturbOn, Person, Place } from '@mui/icons-material'
import moment from 'moment/moment'
import styles from './showRegularSlotDialog.module.scss'
import { Daycare, DaycareType } from '@/types/Daycare'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import DeclareAbsenceRegularSlot from '../../regularSlot/declareAbsenceRegularSlot/declareAbsenceRegularSlot'
import UpdateUniqueRegularSlot from '../../regularSlot/updateUniqueRegularSlot/updateUniqueRegularSlot'
import { useAuth } from '@/modules/context/AuthContext'
import { Role } from '@/types/Role'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import ConfirmDeleteRegularSlotDialog from '../../regularSlot/confirmDeleteRegularSlot/confirmDeleteRegularSlotDialog.tsx'
import { ClosingDay } from '@/types/ClosingDays.ts'
import { isPastDate, titleWithPastLabel } from '@/modules/helper/editionLimitHelper.ts'

interface ShowRegularSlotDialogProps {
    open: boolean
    handleClose: () => void
    startDate: Date | null
    regularSlots: RegularSlot[]
    places: OccasionalSlotsPlaces[]
    daycare: Daycare
    isParent: boolean
    refetchData: () => void
    canEditRegular: boolean
    lastRegularSlotDate: string
    closingDays: ClosingDay[]
}

const ShowRegularSlotDialog: FunctionComponent<ShowRegularSlotDialogProps> = ({
    open,
    handleClose,
    startDate,
    regularSlots,
    daycare,
    isParent,
    refetchData,
    canEditRegular,
    places,
    lastRegularSlotDate,
    closingDays
}: ShowRegularSlotDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const navigate: NavigateFunction = useNavigate()

    const [isConfirmDeleteRegularSlotDialogOpen, setConfirmDeleteRegularSlotDialogOpen] = useState<boolean>(false)
    const [isDeclareAbsenceDialogOpen, setDeclareAbsenceDialogOpen] = useState(false)
    const [isUpdateUniqueRegularSlotDialogOpen, setUpdateUniqueRegularSlotDialogOpen] = useState(false)

    return (
        <>
            <Dialog open={open} handleClose={handleClose}>
                <DialogHeader
                    title={titleWithPastLabel(t('regularSlot'), isPastDate(startDate || ''), t)}
                    parent={isParent}
                />
                <DialogBody className={styles.DialogBody}>
                    <div className={styles.Container}>
                        <CalendarMonth
                            className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                        />
                        <div className={styles.TextContainer}>
                            {startDate && (
                                <p className={styles.FirstText}>
                                    {format(startDate, 'EEEE dd MMMM yyyy', {
                                        locale: getLocalFromLanguage()
                                    })}
                                </p>
                            )}
                            <p className={styles.SecondText}>
                                {t('from') +
                                    ' ' +
                                    moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm') +
                                    ' ' +
                                    t('to').toLowerCase() +
                                    ' ' +
                                    moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm')}
                            </p>
                        </div>
                    </div>
                    <div className={styles.BreakLine}></div>
                    {isRpeOrLaep && user?.role !== Role.parent && regularSlots[0].parentName && (
                        <div className={styles.SecondContainer}>
                            <Person
                                className={`${styles.Icon} ${
                                    isParent ? styles.IconParent : styles.ClickableIconDaycare
                                }`}
                                onClick={() => navigate(`/adults?parent=${regularSlots[0].parentId}`)}
                            />
                            <p className={styles.Text}>{regularSlots[0].parentName}</p>
                        </div>
                    )}
                    {regularSlots.length > 0 &&
                        regularSlots.map((regularSlot: RegularSlot, index: number) => (
                            <div className={styles.SecondContainer} key={index}>
                                <ChildCareOutlined
                                    className={`${styles.Icon} ${
                                        isParent ? styles.IconParent : styles.ClickableIconDaycare
                                    }`}
                                    onClick={() => navigate(`/childs?child=${regularSlot.childId}`)}
                                />
                                <p className={styles.Text}>
                                    {regularSlot.childName +
                                        (regularSlot.childComplement ? ' - ' + regularSlot.childComplement : '')}
                                </p>
                            </div>
                        ))}
                    {regularSlots.length > 0 && regularSlots[0].placeId && (
                        <>
                            {isRpeOrLaep && !isParent && <div className={styles.BreakLine}></div>}
                            <div className={styles.SecondContainer}>
                                <Place
                                    className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                                />
                                <p className={styles.Text}>
                                    {regularSlots[0].placeName + ' ' + regularSlots[0].placeAddress}
                                </p>
                            </div>
                        </>
                    )}
                    {(canEditRegular || isParent) &&
                        ((canEditRegular && moment(startDate).isSameOrAfter(moment().subtract(3, 'months'), 'day')) ||
                            (isParent && moment(startDate).isSameOrAfter(moment(), 'day'))) && (
                            <div className={styles.ButtonContainer}>
                                <ButtonTextIcon
                                    type={'custom'}
                                    onClick={() => setDeclareAbsenceDialogOpen(true)}
                                    customText={t('absence')}
                                    customIcon={<DoDisturbOn />}
                                    parent={isParent}
                                    className={isParent ? styles.ButtonParent : styles.Button}
                                />
                                {!isParent && (
                                    <ButtonTextIcon
                                        type={'delete'}
                                        onClick={() => setConfirmDeleteRegularSlotDialogOpen(true)}
                                        className={styles.Button}
                                    />
                                )}
                                <ButtonTextIcon
                                    type={'edit'}
                                    onClick={() => setUpdateUniqueRegularSlotDialogOpen(true)}
                                    parent={isParent}
                                    className={isParent ? styles.ButtonParent : styles.Button}
                                />
                            </div>
                        )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'close'}
                            onClick={handleClose}
                            parent={isParent}
                            className={styles.ButtonFullWidth}
                        />
                    </div>
                </DialogBody>
            </Dialog>
            {isConfirmDeleteRegularSlotDialogOpen && (
                <ConfirmDeleteRegularSlotDialog
                    open={isConfirmDeleteRegularSlotDialogOpen}
                    handleClose={() => setConfirmDeleteRegularSlotDialogOpen(false)}
                    closeParent={handleClose}
                    regularSlots={regularSlots}
                    date={startDate}
                    daycare={daycare}
                    refetchRegularSlots={refetchData}
                    lastRegularSlotDate={lastRegularSlotDate}
                    closingDays={closingDays}
                />
            )}
            {isDeclareAbsenceDialogOpen && (
                <DeclareAbsenceRegularSlot
                    open={isDeclareAbsenceDialogOpen}
                    handleClose={() => setDeclareAbsenceDialogOpen(false)}
                    isParent={isParent}
                    regularSlots={regularSlots}
                    date={startDate}
                    closeParent={handleClose}
                    refetchData={refetchData}
                    daycare={daycare}
                    isRpeOrLaep={isRpeOrLaep}
                    places={places}
                    lastRegularSlotDate={lastRegularSlotDate}
                    closingDays={closingDays}
                />
            )}
            {isUpdateUniqueRegularSlotDialogOpen && (
                <UpdateUniqueRegularSlot
                    open={isUpdateUniqueRegularSlotDialogOpen}
                    handleClose={() => setUpdateUniqueRegularSlotDialogOpen(false)}
                    isParent={isParent}
                    regularSlots={regularSlots}
                    date={startDate}
                    daycare={daycare}
                    refetchData={refetchData}
                    closeParent={handleClose}
                    lastRegularSlotDate={lastRegularSlotDate}
                    places={places}
                    closingDays={closingDays}
                />
            )}
        </>
    )
}

export default ShowRegularSlotDialog
